import { useCallback, useEffect, useRef, useState } from 'react';

import { useStore } from '@/providers/app-store-provider';

/**
 * @deprecated 使用 swr 替代。如果要缓存全局状态，使用 zustand store 代替。
 */
export function usePageCache({
  pageKey = '',
  initialData = {},
  restoreScroll = true,
  saveScrollOnUnmount = true,
}: {
  pageKey: string;
  initialData: any;
  restoreScroll?: boolean;
  saveScrollOnUnmount?: boolean;
}) {
  const [isFromCache, setIsFromCache] = useState(false);
  const [pageState, setPageState] = useState(initialData);

  // 从全局 zustand store 获取缓存操作方法
  const getPageCache = useStore((state) => state.getPageCache);
  const setPageCacheToStore = useStore((state) => state.setPageCache);
  const updatePageCacheToStore = useStore((state) => state.updatePageCache);
  const initializationRef = useRef({
    getPageCache,
    initialData,
    pageKey,
    restoreScroll,
    setPageCacheToStore,
  });

  // 初始化时从 zustand store 读取数据
  useEffect(() => {
    const {
      getPageCache: getInitialPageCache,
      initialData: initialPageData,
      pageKey: initialPageKey,
      restoreScroll: shouldRestoreScroll,
      setPageCacheToStore: setInitialPageCacheToStore,
    } = initializationRef.current;
    // 从 zustand store 获取缓存数据
    const cachedData = getInitialPageCache(initialPageKey);

    if (cachedData) {
      setIsFromCache(true);
      // 与 SSR / 父组件 initialData 合并，避免缓存里仅有 scrollY 等部分字段时
      // 整体替换掉 SSR 下发的 homeListData，从而引发首页退回空态/骨架
      const merged = { ...initialPageData, ...cachedData };
      setPageState(merged);
      setInitialPageCacheToStore(initialPageKey, merged);

      // 恢复滚动位置
      if (shouldRestoreScroll && merged.scrollY) {
        setTimeout(() => {
          window.scrollTo({ top: merged.scrollY, behavior: 'instant' });
        }, 100);
      }
    } else {
      // 使用初始数据并保存到 store
      setInitialPageCacheToStore(initialPageKey, initialPageData);
      setPageState(initialPageData);
      setIsFromCache(false);
    }
  }, []); // 只执行一次

  // 记录滚动位置
  useEffect(() => {
    const saveScrollPosition = () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const currentData = getPageCache(pageKey) || {};

      // 只有滚动位置发生变化时才更新
      if (currentData.scrollY !== currentScrollY) {
        updatePageCacheToStore(pageKey, { scrollY: currentScrollY });
        setPageState((prev: any) => ({ ...prev, scrollY: currentScrollY }));
      }
    };

    // 监听页面离开事件
    window.addEventListener('beforeunload', saveScrollPosition);

    // 组件卸载时也保存一次
    return () => {
      if (saveScrollOnUnmount) {
        saveScrollPosition();
      }
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [pageKey, getPageCache, saveScrollOnUnmount, updatePageCacheToStore]);

  // 更新页面状态的函数
  const updatePageState = useCallback(
    (newState: any) => {
      // 同时更新到 zustand store
      setPageState((prev: any) => ({ ...prev, ...newState }));
      updatePageCacheToStore(pageKey, newState);
    },
    [pageKey, updatePageCacheToStore],
  );

  // 返回状态和更新函数
  return {
    pageState,
    updatePageState,
    isFromCache,
  };
}
